import {
  Box,
  Button,
  Checkbox,
  COLOR_PALETTE,
  Group,
  Icon,
  Input,
  Modal,
  NativeSelect,
  NumberInput,
  Stack,
  Text,
  useShowNotification,
} from '@inflearn/ds-react';
import { faCalendar } from '@inflearn/pro-light-svg-icons';
import { faMinus } from '@inflearn/pro-solid-svg-icons';
import { DateTimePicker } from '@mantine/dates';
import { useDisclosure } from '@mantine/hooks';
import dayjs from 'dayjs';
import type { ChangeEventHandler } from 'react';
import { useState } from 'react';

import { API_ENDPOINT } from '../../@types/schema';
import { useClientApiV1InstructorsCouponAvailableCoursesQuery } from '../../@types/schema';
import { EVENT_ID } from '../../constants/MODAL_EVENT_ID';
import type { InstructorCouponErrorCode } from '../../hooks/mutations/useCouponsInstructorCreateMutation';
import useCouponsInstructorCreateMutation, {
  INSTRUCTOR_COUPON_ERROR_CODE_KEY,
  INSTRUCTOR_COUPON_ERROR_CODE_MESSAGE,
} from '../../hooks/mutations/useCouponsInstructorCreateMutation';
import { useForAntManModalDisclosure } from '../../hooks/useForAntManModalDisclosure';
import { ApiError } from '@inflearn/datasource-rest';
import { commaPrice } from '../../utils/number';

type ModalEvent = {
  isOpened: boolean;
  handler: {
    open: () => void;
    close: () => void;
    toggle: () => void;
  };
};

export default function InstructorCouponModalWrapper() {
  const [isOpened, handler, eventProps] = useForAntManModalDisclosure({
    eventId: EVENT_ID.INSTRUCTOR_COUPON_MODAL,
    initialState: false,
  });

  const now = eventProps?.now;

  return <InstructorCouponModal isOpened={isOpened} handler={handler} now={now} />;
}

function InstructorCouponModal({ isOpened, handler, now }: ModalEvent & { now?: number }) {
  const [isConfirmOpened, confirmHandler] = useDisclosure(false);
  const { showNotification } = useShowNotification();
  const [isUnlimitedCoupons, setIsUnlimitedCoupons] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState<string>('');
  const [discountValue, setDiscountValue] = useState<number | ''>(0);
  const [limitCount, setLimitCount] = useState<number | ''>();
  const [endedAt, setEndedAt] = useState<Date | null>(
    dayjs(now).add(3, 'day').hour(23).minute(59).second(59).toDate()
  );

  const { mutate } = useCouponsInstructorCreateMutation({
    options: {
      onSuccess: () => {
        confirmHandler.close();
        handler.close();
        window.location.reload();
      },
      onError: (e: unknown) => {
        if (e instanceof ApiError) {
          if (e.errorCode && Object.keys(INSTRUCTOR_COUPON_ERROR_CODE_KEY).includes(e.errorCode)) {
            showNotification({
              title: '쿠폰 발급에 실패했습니다.',
              message: INSTRUCTOR_COUPON_ERROR_CODE_MESSAGE[e.errorCode as InstructorCouponErrorCode],
            });
          } else {
            showNotification({
              title: '쿠폰 발급에 실패했습니다.',
              message: e.message,
            });
          }
          confirmHandler.close();

          return;
        }

        console.error(e);
      },
    },
  });

  const { data } = useClientApiV1InstructorsCouponAvailableCoursesQuery({
    url: API_ENDPOINT.CLIENT_V1_INSTRUCTORS_COUPON_AVAILABLE_COURSES,
    queryKey: [API_ENDPOINT.CLIENT_V1_INSTRUCTORS_COUPON_AVAILABLE_COURSES],
  });

  const handleOpenConfirmModal = () => {
    if (!isValid) {
      return;
    }

    confirmHandler.open();
  };

  const handleSubmit = () => {
    if (!isValid) {
      return;
    }

    mutate({
      courseId: Number(selectedCourseId),
      discountValue,
      limitCount: isUnlimitedCoupons ? 0 : Number(limitCount),
      endedAt: dayjs(endedAt).format('YYYY-MM-DD HH:mm:ss'),
    });
  };

  const handleChangeUnlimitedCoupons = () => {
    if (!isUnlimitedCoupons) {
      setLimitCount('');
    }

    setIsUnlimitedCoupons(!isUnlimitedCoupons);
  };

  const selectedCourse = data?.items.find((c) => c.courseId === Number(selectedCourseId));
  const isValid =
    selectedCourseId && discountValue && (isUnlimitedCoupons || limitCount) && endedAt;

  const handleSelectCourseId: ChangeEventHandler<HTMLSelectElement> = ({ target: { value } }) => {
    setDiscountValue(0);
    setSelectedCourseId(value);
  };

  return (
    <>
      <Modal.Root
        size="md"
        id={EVENT_ID.INSTRUCTOR_COUPON_MODAL}
        opened={isOpened}
        onClose={handler.close}
        closeOnEscape={false}
        closeOnClickOutside={false}>
        <Modal.Overlay />
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>쿠폰 발급</Modal.Title>
            <Modal.CloseButton />
          </Modal.Header>
          <Modal.Body>
            <Stack spacing={24} pb={12}>
              <NativeSelect
                size="md"
                label="쿠폰 발급할 강의"
                description="프로모션에 동의한 강의만 선택할 수 있어요."
                withAsterisk
                value={selectedCourseId}
                data={[
                  { value: '', label: '선택', disabled: true },
                  ...(data?.items.map((c) => ({
                    value: String(c.courseId),
                    label: c.title.length <= 25 ? c.title : `${c.title.slice(0, 25)}...`,
                  })) || []),
                ]}
                onChange={handleSelectCourseId}
              />
              {selectedCourse ? (
                <Input.Wrapper
                  size="md"
                  label="쿠폰 금액"
                  description="모든 금액은 10% 부가세가 포함된 금액이에요."
                  withAsterisk>
                  <Box
                    p={12}
                    mt={10}
                    css={{ backgroundColor: COLOR_PALETTE.gray[1], borderRadius: 8 }}>
                    <Box
                      pb={16}
                      mb={8}
                      css={{ borderBottom: `1px solid ${COLOR_PALETTE.gray[9]}` }}>
                      <Group px={12} py={8}>
                        <Text component="span" weight={600} color="gray.7">
                          강의 금액
                        </Text>
                        <Box ml="auto">
                          <Text component="span" weight={600} color="gray.7">
                            {commaPrice(selectedCourse.surtaxPrice)}원
                          </Text>
                        </Box>
                      </Group>
                      <Group pl={12}>
                        <Text component="span" weight={600} color="gray.7">
                          쿠폰 금액
                        </Text>
                        <Box ml="auto">
                          <NumberInput
                            size="sm"
                            w={182}
                            icon={<Icon icon={faMinus} />}
                            styles={{
                              input: { textAlign: 'right' },
                            }}
                            value={discountValue}
                            onChange={(value) => setDiscountValue(value)}
                            onBlur={({ target: { value } }) => {
                              const parsedValue = parseFloat(value);

                              if (Number.isNaN(parsedValue)) {
                                return;
                              }

                              if (parsedValue > selectedCourse.surtaxPrice) {
                                setDiscountValue(selectedCourse.surtaxPrice);

                                return;
                              }
                              setDiscountValue(Math.floor(parsedValue / 100) * 100);
                            }}
                            withAsterisk
                            hideControls
                            max={selectedCourse.surtaxPrice}
                          />
                        </Box>
                      </Group>
                    </Box>
                    <Box>
                      <Group px={12} py={8}>
                        <Text component="span" weight={600} color="gray.7">
                          최종 금액
                        </Text>
                        <Group ml="auto" align="flex-end" spacing={6}>
                          {!!discountValue &&
                            !!Math.floor((discountValue / selectedCourse.surtaxPrice) * 100) ? (
                            <Text component="span" weight={700} color="red.6">
                              {Math.floor((discountValue / selectedCourse.surtaxPrice) * 100)}%
                            </Text>
                          ) : null}
                          <Text component="span" weight={600} color="gray.7">
                            {commaPrice(selectedCourse.surtaxPrice - (discountValue || 0))}원
                          </Text>
                        </Group>
                      </Group>
                    </Box>
                  </Box>
                </Input.Wrapper>
              ) : null}

              <NumberInput
                size="md"
                hideControls
                value={limitCount}
                onChange={(value) => setLimitCount(value)}
                styles={{
                  label: { width: '100%' },
                }}
                label={
                  <Group mb={10}>
                    <Text component="span" color="gray.9">
                      쿠폰 개수
                    </Text>
                    <Text component="span" color="red.7" ml={3}>
                      *
                    </Text>
                    <Checkbox
                      size="sm"
                      radius="sm"
                      checked={isUnlimitedCoupons}
                      onChange={handleChangeUnlimitedCoupons}
                      labelPosition="left"
                      ml="auto"
                      label={
                        <Text component="span" color="gray.7" weight={400}>
                          무제한
                        </Text>
                      }
                    />
                  </Group>
                }
                placeholder={isUnlimitedCoupons ? '무제한' : '최대 999까지 입력 가능'}
                parser={(value) => value.replace(/(,*)/g, '')}
                formatter={(value) =>
                  !Number.isNaN(parseFloat(value))
                    ? `${value}`.replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ',')
                    : ''
                }
                max={999}
                disabled={isUnlimitedCoupons}
              />
              <Box>
                <DateTimePicker
                  size="md"
                  value={endedAt}
                  onChange={setEndedAt}
                  icon={<Icon icon={faCalendar} />}
                  withAsterisk
                  valueFormat="YYYY.MM.DD. HH:mm:ss"
                  label="쿠폰 발급 만료일"
                  description="쿠폰 발급 만료일은 유저가 쿠폰을 발급할 수 있는 최대 기한이에요."
                  placeholder="~ YYYY. MM. DD. 23:59:59"
                  minDate={dayjs(now).add(3, 'day').toDate()}
                  popoverProps={{
                    withinPortal: true,
                  }}
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
                <Text c="gray.6" mt={2}>
                  발급된 쿠폰은 수강생이 쿠폰함에 등록한 시점부터 24시간 내에 사용할 수 있어요.
                </Text>
              </Box>
            </Stack>
          </Modal.Body>
          <Modal.Footer align="grow">
            <Button variant="filled" size="md" disabled={!isValid} onClick={handleOpenConfirmModal}>
              {discountValue ? <>{commaPrice(discountValue)}원 </> : ''}
              쿠폰 발급하기
            </Button>
          </Modal.Footer>
        </Modal.Content>
      </Modal.Root>
      <Modal.Root opened={isConfirmOpened} onClose={confirmHandler.close} size="xs">
        <Modal.Overlay />
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>쿠폰 발급 안내</Modal.Title>
            <Modal.CloseButton />
          </Modal.Header>
          <Modal.Body>
            이미 발행 된 쿠폰은 다시 수정할 수 없어요. 입력한 내용으로 쿠폰을 발급할까요?
          </Modal.Body>
          <Modal.Footer align="grow">
            <Button variant="default" size="md" onClick={confirmHandler.close}>
              취소
            </Button>
            <Button variant="filled" size="md" onClick={handleSubmit}>
              발급하기
            </Button>
          </Modal.Footer>
        </Modal.Content>
      </Modal.Root>
    </>
  );
}
